import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, animeList } from '@/mocks/anime';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'year'>('rating');

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredAnime = useMemo(() => {
    let list = animeList.filter((a) => a.category === slug);
    if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'views') {
      list = [...list].sort((a, b) => b.views - a.views);
    } else {
      list = [...list].sort((a, b) => b.year - a.year);
    }
    return list;
  }, [slug, sortBy]);

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAnimeClick = (id: string) => {
    navigate(`/article/${id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSortClick = (type: 'rating' | 'views' | 'year') => {
    setSortBy(type);
  };

  return (
    <>
      <SEO
        title={`${currentCategory.name}`}
        description={`天天动漫${currentCategory.name}，为你精选热门动漫番剧，高清在线观看。涵盖热血、搞笑、治愈、悬疑等多种类型，满足你的追番需求。`}
        keywords={`${currentCategory.name},天天动漫,动漫分类,在线追番,番剧推荐`}
        canonicalPath={`/category/${slug}`}
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `天天动漫 - ${currentCategory.name}`,
          description: `天天动漫${currentCategory.name}，精选热门番剧高清在线观看。`,
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb items={[{ name: '首页', href: '/' }, { name: currentCategory.name }]} />

            <div className="mt-6 mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-600 mt-2">
                共 {filteredAnime.length} 部作品
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-6">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                    <i className={cat.icon}></i>
                  </span>
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 mb-8">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              {[
                { key: 'rating' as const, label: '评分最高' },
                { key: 'views' as const, label: '最受欢迎' },
                { key: 'year' as const, label: '最新发布' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => handleSortClick(opt.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-secondary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5"
              data-product-shop
            >
              {filteredAnime.map((anime) => (
                <div
                  key={anime.id}
                  className="group cursor-pointer"
                  onClick={() => handleAnimeClick(anime.id)}
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-100">
                    <img
                      src={anime.cover}
                      alt={anime.title}
                      title={`${anime.title} - ${currentCategory.name}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-md whitespace-nowrap">
                        {anime.rating}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <p className="text-white text-xs whitespace-nowrap">{anime.episodes}</p>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {anime.title}
                  </h3>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {anime.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {filteredAnime.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无作品</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}